import { ComponentChild } from 'preact';
import { useTranslator } from '@eo-locale/preact';
import clsx from 'clsx';
import { ChangeEvent, createElement } from 'preact/compat';
import { useCallback } from 'preact/hooks';
import { useUniqueId } from 'utilities/use-unique-id';

import { helpTextID, labelID, Labelled } from 'components/Labelled';
import { Text } from 'components/Text/Text';

import styles from './TextField.module.scss';

type InputType = 'text' | 'email';

interface TextFieldProps {
  autocomplete?: HTMLInputElement['autocomplete'];
  label: string;
  labelIcon?: ComponentChild;
  /** Type of the input
   * @default 'text'
   */
  type?: InputType;
  value: string;
  placeholder?: string;
  name?: string;
  multiline?: boolean | number;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  helpText?: string;
  showCharacterCount?: boolean;
  error?: string;
  onChange: (value: string) => void;
}

export function TextField({
  autocomplete = 'off',
  label,
  labelIcon,
  type = 'text',
  value,
  placeholder,
  name,
  multiline,
  required,
  disabled,
  maxLength,
  helpText,
  showCharacterCount,
  error,
  onChange,
}: TextFieldProps) {
  const { translate } = useTranslator();
  const id = useUniqueId('TextField');
  const labelId = labelID(id);
  const helpTextId = helpTextID(id);

  let className = null;

  if (showCharacterCount)
    className = clsx(
      styles.TextField,
      styles.TextFieldWithCharacterCount,
      error && styles.hasError,
      disabled && styles.disabled,
    );
  else
    className = clsx(
      styles.TextField,
      error && styles.hasError,
      disabled && styles.disabled,
    );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.currentTarget.value);
    },
    [onChange],
  );

  const describedBy: string[] = [];

  if (error) {
    describedBy.push(`${id}Error`);
  }

  if (showCharacterCount) {
    describedBy.push(`${id}CharacterCount`);
  }

  if (helpText) {
    describedBy.push(helpTextId);
  }

  let characterCountMarkup = null;

  if (showCharacterCount) {
    const characterCount = value.length;
    const characterCountText = !maxLength
      ? characterCount
      : `${characterCount}/${maxLength} ${translate(
          'write_review_characters_label',
          {
            defaultMessage: 'characters',
          },
        )}`;

    characterCountMarkup = (
      <div className={styles.CharacterCount} id={`${id}CharacterCount`}>
        <Text as="span" size="small" variation="subdued">
          {characterCountText}
        </Text>
      </div>
    );
  }

  const input = createElement(multiline ? 'textarea' : 'input', {
    type: !multiline ? type : undefined,
    autocomplete,
    placeholder,
    className: styles.Control,
    rows: getRows(multiline),
    value,
    name,
    disabled,
    maxLength,
    'aria-labelledby': labelId,
    'aria-describedby': describedBy.length ? describedBy.join(' ') : undefined,
    'aria-invalid': Boolean(error),
    'aria-multiline': false,
    'aria-required': required,
    onChange: handleChange as any,
  });

  const inputClasses = [styles.Input];

  if (showCharacterCount) inputClasses.push(styles.InputWithCharacterCount);

  return (
    <Labelled
      id={id}
      label={label}
      icon={labelIcon}
      error={error}
      helpText={helpText}
      required={required}
    >
      <div className={className}>
        <div className={clsx(inputClasses)}>
          {input}
          <div className={styles.Backdrop}>&nbsp;</div>
        </div>
        {characterCountMarkup}
      </div>
    </Labelled>
  );
}

function getRows(multiline?: boolean | number) {
  if (!multiline) return undefined;

  return typeof multiline === 'number' ? multiline : 1;
}
